import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
    HydrationBoundary,
    QueryClient,
    QueryClientProvider,
    type DehydratedState,
} from "@tanstack/react-query";
import { Analytics } from "@vercel/analytics/react";
import { Suspense, useState, type ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, matchPath, Route, Routes } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { lazyRoute, type PreloadableComponent } from "./lib/lazyRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Home (Index) + NotFound stay eager (always needed). Every other route is
// code-split so the home load no longer ships the JS for all ~40 pages. The
// lazyRoute wrapper keeps this compatible with our build-time prerender + client
// hydration — see src/lib/lazyRoute.tsx.
const AiTeam = lazyRoute(() => import("./pages/AiTeam"));
const LumaAgent = lazyRoute(() => import("./pages/ai-team/Luma"));
const VexoAgent = lazyRoute(() => import("./pages/ai-team/Vexo"));
const NovaAgent = lazyRoute(() => import("./pages/ai-team/Nova"));
const SketchAgent = lazyRoute(() => import("./pages/ai-team/Sketch"));
const FluxAgent = lazyRoute(() => import("./pages/ai-team/Flux"));
const KodaAgent = lazyRoute(() => import("./pages/ai-team/Koda"));
const LoopAgent = lazyRoute(() => import("./pages/ai-team/Loop"));
const QuillAgent = lazyRoute(() => import("./pages/ai-team/Quill"));
const BuzzAgent = lazyRoute(() => import("./pages/ai-team/Buzz"));
const BackgroundReplacer = lazyRoute(
    () => import("./pages/BackgroundReplacer"),
);
const CaptionVideos = lazyRoute(() => import("./pages/CaptionVideos"));
const CgiVideos = lazyRoute(() => import("./pages/CgiVideos"));
const DukaanAds = lazyRoute(() => import("./pages/DukaanAds"));
const EditWithAi = lazyRoute(() => import("./pages/EditWithAi"));
const FashionReels = lazyRoute(() => import("./pages/FashionReels"));
const FlatLay = lazyRoute(() => import("./pages/FlatLay"));
const Gallery = lazyRoute(() => import("./pages/Gallery"));
const GhostMannequin = lazyRoute(() => import("./pages/GhostMannequin"));
const Pricing = lazyRoute(() => import("./pages/Pricing"));
const PrivacyPolicy = lazyRoute(() => import("./pages/PrivacyPolicy"));
const ProductShowcase = lazyRoute(() => import("./pages/ProductShowcase"));
const ProductVideos = lazyRoute(() => import("./pages/ProductVideos"));
const RemoveBackground = lazyRoute(() => import("./pages/RemoveBackground"));
const Resize = lazyRoute(() => import("./pages/Resize"));
const About = lazyRoute(() => import("./pages/resources/About"));
const Apis = lazyRoute(() => import("./pages/resources/Apis"));
const BlogDetail = lazyRoute(() => import("./pages/resources/BlogDetail"));
const Blogs = lazyRoute(() => import("./pages/resources/Blogs"));
const Contact = lazyRoute(() => import("./pages/resources/Contact"));
const Accessories = lazyRoute(() => import("./pages/solutions/Accessories"));
const Automobile = lazyRoute(() => import("./pages/solutions/Automobile"));
const Ecommerce = lazyRoute(() => import("./pages/solutions/Ecommerce"));
const Fashion = lazyRoute(() => import("./pages/solutions/Fashion"));
const Jewellery = lazyRoute(() => import("./pages/solutions/Jewellery"));
const TalkingAvatars = lazyRoute(() => import("./pages/TalkingAvatars"));
const TermsAndConditions = lazyRoute(
    () => import("./pages/TermsAndConditions"),
);
const ToolLandingDynamic = lazyRoute(
    () => import("./pages/ToolLandingDynamic"),
);
const UgcVideos = lazyRoute(() => import("./pages/UgcVideos"));
const Upscale = lazyRoute(() => import("./pages/Upscale"));
const UpscaleVideo = lazyRoute(() => import("./pages/UpscaleVideo"));
const VirtualTryOn = lazyRoute(() => import("./pages/VirtualTryOn"));

// path → lazy component, used only to preload the right chunk(s).
const LAZY_ROUTES: { path: string; Component: PreloadableComponent }[] = [
    { path: "/dukaan-ads", Component: DukaanAds },
    { path: "/:lang/dukaan-ads", Component: DukaanAds },
    { path: "/gallery", Component: Gallery },
    { path: "/virtual-try-on", Component: VirtualTryOn },
    { path: "/product-videos", Component: ProductVideos },
    { path: "/talking-avatars", Component: TalkingAvatars },
    { path: "/ugc-videos", Component: UgcVideos },
    { path: "/upscale-video", Component: UpscaleVideo },
    { path: "/caption-videos", Component: CaptionVideos },
    { path: "/fashion-reels", Component: FashionReels },
    { path: "/cgi-videos", Component: CgiVideos },
    { path: "/background-replacer", Component: BackgroundReplacer },
    { path: "/product-showcase", Component: ProductShowcase },
    { path: "/ghost-mannequin", Component: GhostMannequin },
    { path: "/flat-lay", Component: FlatLay },
    { path: "/remove-background", Component: RemoveBackground },
    { path: "/upscale", Component: Upscale },
    { path: "/resize", Component: Resize },
    { path: "/edit-with-ai", Component: EditWithAi },
    { path: "/solutions/fashion", Component: Fashion },
    { path: "/solutions/ecommerce", Component: Ecommerce },
    { path: "/solutions/accessories", Component: Accessories },
    { path: "/solutions/jewellery", Component: Jewellery },
    { path: "/solutions/automobile", Component: Automobile },
    { path: "/about", Component: About },
    { path: "/ai-team", Component: AiTeam },
    { path: "/ai-team/luma", Component: LumaAgent },
    { path: "/ai-team/vexo", Component: VexoAgent },
    { path: "/ai-team/nova", Component: NovaAgent },
    { path: "/ai-team/sketch", Component: SketchAgent },
    { path: "/ai-team/flux", Component: FluxAgent },
    { path: "/ai-team/koda", Component: KodaAgent },
    { path: "/ai-team/loop", Component: LoopAgent },
    { path: "/ai-team/quill", Component: QuillAgent },
    { path: "/ai-team/buzz", Component: BuzzAgent },
    { path: "/blogs", Component: Blogs },
    { path: "/blogs/:slug", Component: BlogDetail },
    { path: "/tools/:slug", Component: ToolLandingDynamic },
    { path: "/apis", Component: Apis },
    { path: "/contact", Component: Contact },
    { path: "/pricing", Component: Pricing },
    { path: "/privacy-policy", Component: PrivacyPolicy },
    { path: "/terms-and-conditions", Component: TermsAndConditions },
];

/** Preload every route chunk. The prerender calls this before renderToString so
 *  each route's HTML is fully baked (no Suspense fallback in the static output). */
export const preloadAllRoutes = () =>
    Promise.all(LAZY_ROUTES.map(({ Component }) => Component.preload()));

/** Preload the chunk for the route matching `pathname`. The client calls this
 *  before hydration so the first render matches the prerendered HTML. */
export const preloadRouteForPath = (pathname: string) => {
    const match = LAZY_ROUTES.find((r) => matchPath(r.path, pathname));
    return match ? match.Component.preload() : Promise.resolve();
};

interface AppProvidersProps {
    queryClient?: QueryClient;
    dehydratedState?: DehydratedState;
    helmetContext?: Record<string, unknown>;
    children: ReactNode;
}

export const createPixroQueryClient = () =>
    new QueryClient({
        defaultOptions: {
            queries: {
                staleTime: 60_000,
                gcTime: 5 * 60_000,
                retry: 1,
                refetchOnWindowFocus: false,
            },
        },
    });

const AppRoutes = () => (
    <Suspense fallback={null}>
        <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/dukaan-ads" element={<DukaanAds />} />
            <Route path="/:lang/dukaan-ads" element={<DukaanAds />} />
            <Route path="/gallery" element={<Gallery />} />

            <Route path="/virtual-try-on" element={<VirtualTryOn />} />
            <Route path="/product-videos" element={<ProductVideos />} />
            <Route path="/talking-avatars" element={<TalkingAvatars />} />
            <Route path="/ugc-videos" element={<UgcVideos />} />
            <Route path="/upscale-video" element={<UpscaleVideo />} />
            <Route path="/caption-videos" element={<CaptionVideos />} />
            <Route path="/fashion-reels" element={<FashionReels />} />
            <Route path="/cgi-videos" element={<CgiVideos />} />
            <Route
                path="/background-replacer"
                element={<BackgroundReplacer />}
            />
            <Route path="/product-showcase" element={<ProductShowcase />} />
            <Route path="/ghost-mannequin" element={<GhostMannequin />} />
            <Route path="/flat-lay" element={<FlatLay />} />
            <Route path="/remove-background" element={<RemoveBackground />} />
            <Route path="/upscale" element={<Upscale />} />
            <Route path="/resize" element={<Resize />} />
            <Route path="/edit-with-ai" element={<EditWithAi />} />

            <Route path="/solutions/fashion" element={<Fashion />} />
            <Route path="/solutions/ecommerce" element={<Ecommerce />} />
            <Route path="/solutions/accessories" element={<Accessories />} />
            <Route path="/solutions/jewellery" element={<Jewellery />} />
            <Route path="/solutions/automobile" element={<Automobile />} />

            <Route path="/about" element={<About />} />
            <Route path="/ai-team" element={<AiTeam />} />
            <Route path="/ai-team/luma" element={<LumaAgent />} />
            <Route path="/ai-team/vexo" element={<VexoAgent />} />
            <Route path="/ai-team/nova" element={<NovaAgent />} />
            <Route path="/ai-team/sketch" element={<SketchAgent />} />
            <Route path="/ai-team/flux" element={<FluxAgent />} />
            <Route path="/ai-team/koda" element={<KodaAgent />} />
            <Route path="/ai-team/loop" element={<LoopAgent />} />
            <Route path="/ai-team/quill" element={<QuillAgent />} />
            <Route path="/ai-team/buzz" element={<BuzzAgent />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:slug" element={<BlogDetail />} />
            {/* Data-driven tool landing pages (CMS-backed, /api/tool-pages) */}
            <Route path="/tools/:slug" element={<ToolLandingDynamic />} />
            <Route path="/apis" element={<Apis />} />
            <Route path="/contact" element={<Contact />} />

            <Route path="/pricing" element={<Pricing />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
                path="/terms-and-conditions"
                element={<TermsAndConditions />}
            />

            <Route path="*" element={<NotFound />} />
        </Routes>
    </Suspense>
);

const AppProviders = ({
    children,
    queryClient,
    dehydratedState,
    helmetContext,
}: AppProvidersProps) => {
    const resolvedQueryClient = queryClient ?? createPixroQueryClient();

    return (
        <HelmetProvider context={helmetContext}>
            <QueryClientProvider client={resolvedQueryClient}>
                <HydrationBoundary state={dehydratedState}>
                    <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        {children}
                    </TooltipProvider>
                </HydrationBoundary>
            </QueryClientProvider>
        </HelmetProvider>
    );
};

const App = ({
    dehydratedState,
}: Pick<AppProvidersProps, "dehydratedState"> = {}) => {
    const [queryClient] = useState(() => createPixroQueryClient());

    return (
        <AppProviders
            queryClient={queryClient}
            dehydratedState={dehydratedState}
        >
            <Analytics />
            <BrowserRouter>
                <ScrollToTop />
                <AppRoutes />
            </BrowserRouter>
        </AppProviders>
    );
};

export { AppProviders, AppRoutes };
export default App;
